import { Avatar, Badge, Box, Button, Container, Flex, Group, Menu, rem, Text } from '@mantine/core';
import { CaretDownIcon, CaretRightIcon, SignOutIcon, UserIcon, UserSwitchIcon } from '@phosphor-icons/react';
import type { ComponentProps, FC } from 'react';
import { Link } from 'react-router';

import { LanguagesDropdown } from '../LanguagesDropdown/LanguagesDropdown.tsx';
import type { LanguagesDropdownProps } from '../LanguagesDropdown/LanguagesDropdown.tsx';
import { Logo } from '../Logo/Logo.tsx';
import { MenuItem } from '../MenuItem/MenuItem.tsx';

const initials = (name: string) => {
  return name
    .split(' ')
    .map((namePart) => namePart[0])
    .join('');
};

interface MenuItemsProps {
  languages: LanguagesDropdownProps['languages'];
  activeLocale: LanguagesDropdownProps['activeLocale'];
  setActiveLocale: LanguagesDropdownProps['setActiveLocale'];
  onLogout?: () => void;
  menuItems: ComponentProps<typeof MenuItem>[];
  user?: { name?: string; promoterName?: string; email?: string };
  adminUser?: { name: string | null; email: string | null };
  adminBannerOffset?: number;
}

export const Header: FC<MenuItemsProps> = ({
  menuItems,
  languages,
  activeLocale,
  setActiveLocale,
  user,
  adminUser,
  adminBannerOffset = 0,
  onLogout,
}) => {
  return (
    <Box
      bg="white"
      left={0}
      pos="fixed"
      right={0}
      style={{
        zIndex: 10,
        borderBottom: '1px solid #E8E8E8',
        maxWidth: '100%',
      }}
      top={adminBannerOffset}
    >
      <Container size="lg">
        <Flex align="center" h={110} justify="space-between">
          <Box style={{ whiteSpace: 'none' }}>
            <Link style={{ textDecoration: 'none', display: 'block', height: 32 }} to="/">
              <Logo />
            </Link>
          </Box>

          <Flex component="ul" display="block" flex="1" gap="xl" justify="center">
            {menuItems.map((menuItem) => (
              <Box component="li" key={`${menuItem.text}-${menuItem.to}`} style={{ listStyle: 'none' }}>
                <MenuItem {...menuItem} />
              </Box>
            ))}
          </Flex>

          <Box style={{ whiteSpace: 'none' }}>
            <LanguagesDropdown activeLocale={activeLocale} languages={languages} setActiveLocale={setActiveLocale} />
            {user != null && (
              <Menu
                withArrow
                position="bottom-end"
                radius="xs"
                shadow="md"
                styles={{
                  // The theme scales spacing and font sizes up, which Mantine's menu defaults
                  // inherit: a 24px icon gap and a 20px label. Both are far too large here.
                  divider: { marginBlock: rem(8) },
                  dropdown: { padding: rem(8) },
                  item: { borderRadius: rem(12), padding: `${rem(10)} ${rem(12)}` },
                  itemSection: { marginInlineEnd: rem(10) },
                }}
              >
                <Menu.Target>
                  <Button color="dark.3" size="lg" type="button" variant="outline">
                    <Group gap="xxxs">
                      <UserIcon size="28" />
                      <Text size="xs">{user.name}</Text>
                      <CaretDownIcon size="16" />
                    </Group>
                  </Button>
                </Menu.Target>

                <Menu.Dropdown miw={320}>
                  <Flex align="center" gap="xxs" px={rem(12)} py={rem(10)}>
                    <Avatar
                      radius="xl"
                      size={48}
                      styles={{
                        placeholder: {
                          backgroundColor: '#F6E6FA',
                          color: '#A02FB0',
                          fontSize: rem(17),
                          fontWeight: 700,
                        },
                      }}
                    >
                      {user.name ? initials(user.name) : 'G'}
                    </Avatar>

                    <Box style={{ minWidth: 0 }}>
                      <Text truncate fw={700} size="xxs">
                        {user.name}
                      </Text>

                      {user.email && (
                        <Text truncate c="gigi-grey-text" size="xxs">
                          {user.email}
                        </Text>
                      )}

                      {user.promoterName && (
                        <Badge
                          color="grape"
                          maw={200}
                          mt={6}
                          radius="xl"
                          size="sm"
                          styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          tt="none"
                          variant="light"
                        >
                          {user.promoterName}
                        </Badge>
                      )}
                    </Box>
                  </Flex>

                  <Menu.Divider />

                  {adminUser != null && (
                    <>
                      <Menu.Label fz={rem(13)}>Signed in as admin {adminUser.email}</Menu.Label>
                      <Menu.Item
                        component={Link}
                        fz="xxs"
                        leftSection={<UserSwitchIcon size={20} />}
                        rightSection={<CaretRightIcon size={16} />}
                        to="/log-in-as"
                      >
                        Switch promoter
                      </Menu.Item>
                    </>
                  )}

                  <Menu.Item
                    component={Link}
                    fz="xxs"
                    leftSection={<UserIcon size={20} />}
                    rightSection={<CaretRightIcon size={16} />}
                    to="/account"
                  >
                    Account settings
                  </Menu.Item>

                  {onLogout != null && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        fz="xxs"
                        leftSection={<SignOutIcon size={20} />}
                        onClick={() => onLogout?.()}
                      >
                        Logout
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};
